import React, {
    FormEvent,
    ReactElement, useEffect, useState,
} from "react";
import { Form } from "react-bootstrap";
import BooleanSelect from "../../../../components/pickers/boolean/BooleanSelect";
import TagList from "../../../../components/tag-list/TagList";
import TimedMessage from "../../../../components/timed-message/TimedMessage";
import {
    CloseSettingsDocument, GetSettingsDocument, RequiredField, TagsSettingsDocument, useSaveSettingsMutation,
} from "../../../../__generated__/api";
import { SettingsProps } from "../base";

export default function Customization(props: SettingsProps): ReactElement {
    const [save] = useSaveSettingsMutation();

    const [requiredCloseFields, setRequiredCloseFields] = useState(props.settings?.customization?.requiredCloseFields);
    const [tags, setTags] = useState(props.settings?.customization?.tags);
    const [isSaved, setIsSaved] = useState(false);

    useEffect(() => {
        setRequiredCloseFields(props.settings?.customization?.requiredCloseFields);
        setTags(props.settings?.customization?.tags);
    }, [props.settings]);

    const onSubmit = (event: FormEvent) => {
        event.preventDefault();
        (async () => {
            await save({
                refetchQueries: [
                    { query: CloseSettingsDocument },
                    { query: TagsSettingsDocument },
                    { query: GetSettingsDocument },
                ],
                variables: {
                    customization: {
                        requiredCloseFields,
                        tags,
                    },
                },
            });
            setIsSaved(true);
        })();
    };

    const createRequiredFieldHandler = (field: RequiredField) => (value: boolean) => {
        let copy = Array.from(requiredCloseFields ?? []);
        if (value) {
            copy.push(field);
        } else {
            copy = copy.filter(item => item !== field);
        }
        setRequiredCloseFields(copy.dedupe());
    };

    return (
        <form className="flex-grow-1" onSubmit={onSubmit}>

            <div className="border p-3 rounded mb-4 bg-white">
                <TimedMessage shouldShow={isSaved} onClose={() => setIsSaved(false)}>
                    Settings Saved
                </TimedMessage>
                <h5 className="mb-3 text-light">
                    Required Fields
                </h5>

                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder">Require Tags to close</Form.Label>
                    <BooleanSelect
                        className="text-gray"
                        isRequired
                        value={requiredCloseFields?.includes(RequiredField.Tags) ?? false}
                        onChange={createRequiredFieldHandler(RequiredField.Tags)}
                    />
                    <Form.Text>
                        Should tags be required to end a conversation?
                    </Form.Text>
                </Form.Group>
                <Form.Group>
                    <Form.Label className="fw-bolder">Require Notes to close</Form.Label>
                    <BooleanSelect
                        className="text-gray"
                        isRequired
                        value={requiredCloseFields?.includes(RequiredField.TicketNotes) ?? false}
                        onChange={createRequiredFieldHandler(RequiredField.TicketNotes)}
                    />
                    <Form.Text>
                        Should comments be required to end a conversation?
                    </Form.Text>
                </Form.Group>
            </div>
            <div className="border p-3 rounded mb-5 bg-white">
                <h5 className="mb-3 text-light">
                    Other Settings
                </h5>

                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder">Tags</Form.Label>
                    <TagList
                        value={tags as string[] ?? []}
                        onChange={value => setTags(value)}
                        isTag
                        canNotHaveDuplicates
                        shouldSort
                    />
                    <Form.Text>
                        Which tags are available to when creating a ticket?
                    </Form.Text>
                </Form.Group>

            </div>

            <div className="d-flex justify-content-end mt-3 fixed-bottom border-top p-3 bg-white">
                <button type="submit" className="btn btn-primary">
                    Save
                </button>
            </div>

        </form>
    );
}
